import { Layout, PermDefinitionConstants } from "@mcleod/components";
import { Api, ArrayUtil, getApiMetadata, LogManager, ModelRow, Mutex, ObjectUtil } from "@mcleod/core";
import { RowPermsAllowDeny } from "../models/ModelPermsAllowDeny";
import { RowPermsSecureItem } from "../models/ModelPermsSecureItem";
import { PermissionsKeyGenerator } from "./PermissionsKeyGenerator";
import { SecureItem } from "./SecureItem";
import { PermsUserAndGroupCache } from "./PermsUserAndGroupCache ";

const log = LogManager.getLogger("common.permissions.PermissionsCache");


export class PermissionsSource {
    private secureItemRows: Map<string, RowPermsSecureItem>;
    private allowDenyRows: Map<string, Map<string, RowPermsAllowDeny>>;
    private deletedSecureItems: Map<string, RowPermsSecureItem>;
    private deletedAllowDenyRows: Map<string, RowPermsAllowDeny>;
    private postMutex = new Mutex();
    private _changedDataCallbacks: ((hasChanged: boolean) => void)[] = [];
    private _permsUserCache = new PermsUserAndGroupCache();

    public get permsUserCache(): PermsUserAndGroupCache {
        return this._permsUserCache;
    }

    addChangedDataCallback(callback: (hasChanged: boolean) => void) {
        this._changedDataCallbacks.push(callback);
    }

    removeChangedDataCallback(callback: (hasChanged: boolean) => void) {
        const index = this._changedDataCallbacks.indexOf(callback);
        if (index >= 0)
            this._changedDataCallbacks.splice(index, 1);
    }

    private fireChangedDataCallbacks(hasChagned = this.hasChangedData()) {
        this._changedDataCallbacks.forEach(cb => cb(hasChagned));
    }

    public hasChangedData() {
        return this.deletedSecureItems.size > 0
            || this.deletedAllowDenyRows.size > 0
            || this.hasAppendingSecureItemRows()
            || this.hasAppendingAllowDenyRows();
    }

    private getAppendingSecureItemRows(): RowPermsSecureItem[] {
        return this.getSecureItemRows(row => row._appending === true);
    }

    private hasAppendingSecureItemRows(): boolean {
        return Array.from(this.secureItemRows.values()).some(row => row._appending === true);
    }

    private hasAppendingAllowDenyRows(): boolean {
        return Array.from(this.allowDenyRows.values())
            .some(innerMap => Array.from(innerMap.values()).some(row => row._appending === true));
    }

    private getAppendingAllowDenyRows(): RowPermsAllowDeny[] {
        return Array.from(this.allowDenyRows.values())
            .flatMap(innerMap => Array.from(innerMap.values()))
            .filter(row => row._appending === true);
    }

    async loadPermissionsData(): Promise<void> {
        await getApiMetadata("common/perms-allow-deny");
        await getApiMetadata("common/perms-secure-item");
        const response = await Api.search("common/perms-manager");
        this.setPermissionsDataFromResponse(response);
    }

    private setPermissionsDataFromResponse(response: any) {
        this.secureItemRows = new Map();
        this.allowDenyRows = new Map();
        this.deletedSecureItems = new Map();
        this.deletedAllowDenyRows = new Map();

        const data = response?.data?.[0];
        (data?.perm_secure_item_rows ?? []).forEach(row => {
            const secureItem = RowPermsSecureItem.create(false, row);
            this.addToSecureItemMap(secureItem);
        });

        (data?.perm_allow_deny_rows ?? []).forEach(row => {
            const allowDenyRow = RowPermsAllowDeny.create(false, row);
            this.addToAllowDenyMap(allowDenyRow);
        });

        this.permsUserCache.populateUserGroupData(data.user_groups);
        this.permsUserCache.populateUserData(data.users);
    }

    private addToSecureItemMap(secureItem: RowPermsSecureItem) {
        const keyString = PermissionsKeyGenerator.createKey(secureItem);
        if (!this.secureItemRows.has(keyString)) {
            this.secureItemRows.set(keyString, secureItem);
        }
    }

    private addToAllowDenyMap(allowDenyRow: RowPermsAllowDeny) {
        const outerKey = PermissionsKeyGenerator.createKey(allowDenyRow);
        const innerKey = PermissionsKeyGenerator.createAllowDenyKey(allowDenyRow);
        let innerMap = this.allowDenyRows.get(outerKey);
        if (!innerMap) {
            innerMap = new Map();
            this.allowDenyRows.set(outerKey, innerMap);
        }

        if (!innerMap.has(innerKey)) {
            innerMap.set(innerKey, allowDenyRow);
        }
    }

    private getPageIdentifier(row: RowPermsSecureItem) {
        if (row.isActionPerm())
            return row.get("item_name");
        return row.get("screen_class");
    }

    getSecureItemRows(predicate: (row: RowPermsSecureItem) => boolean): RowPermsSecureItem[] {
        const rows = Array.from(this.secureItemRows.values());
        return rows.filter(predicate);
    }

    getSecureItemRowsForPage(pageIdentifier: string): RowPermsSecureItem[] {
        if (pageIdentifier == null)
            return null;
        return this.getSecureItemRows(row => this.getPageIdentifier(row) === pageIdentifier);
    }

    getEntireScreenSecureItemRow(pageIdentifier: string): RowPermsSecureItem {
        const key = PermissionsKeyGenerator.createKey({
            screen_class: pageIdentifier,
            item_name: RowPermsSecureItem.ENTIRE_SCREEN,
            security_type: PermDefinitionConstants.ENTIRE_SCREEN.permsType
        });

        return this.secureItemRows.get(key);
    }

    getFieldLevelSecureItemRows(pageIdentifier: string): RowPermsSecureItem[] {
        return this.getSecureItemRowsForPage(pageIdentifier).filter(row => row.isFieldLevel());
    }

    hasFieldLevelSecureItemRows(pageIdentifier: string): boolean {
        return this.getFieldLevelSecureItemRows(pageIdentifier)?.length > 0;
    }

    getSecureItemRow(secureItem: SecureItem): RowPermsSecureItem {
        return this.secureItemRows.get(secureItem.createKeyString());
    }

    getAllowDenyRows(secureItem: SecureItem): RowPermsAllowDeny[] {
        const key = secureItem.createKeyString();
        return Array.from(this.allowDenyRows?.get(key)?.values() ?? []);
    }

    addSecureItemRow(secureItem: SecureItem): RowPermsSecureItem {
        let hasChanged: boolean;
        try {

            const keyString = secureItem.createKeyString();
            const previouslyDeleted = this.deletedSecureItems.get(keyString);

            if (previouslyDeleted) {
                this.deletedSecureItems.delete(keyString);
                this.secureItemRows.set(keyString, previouslyDeleted);
                return previouslyDeleted;
            }

            const existingRow = this.secureItemRows.get(keyString)
            if (existingRow) {
                return existingRow;
            }

            const rowToAdd = secureItem.createRowPermsSecureItem();

            this.secureItemRows.set(keyString, rowToAdd);
            hasChanged = true;
            return rowToAdd;
        } finally {
            secureItem?.permsChanged(false);
            this.fireChangedDataCallbacks(hasChanged);
        }
    }

    deleteSecureItem(secureItem: SecureItem) {
        if (secureItem == null) return;
        const key = secureItem.createKeyString();

        try {
            const row = this.secureItemRows.get(key);

            if (row != null) {
                this.secureItemRows.delete(key);

                if (row._appending !== false)
                    return;

                this.deletedSecureItems.set(key, row);
            }
        } finally {
            secureItem?.permsChanged(true);
            this.fireChangedDataCallbacks();
        }
    }

    addAllowDenyRow(row: RowPermsAllowDeny): RowPermsAllowDeny {
        if (row == null)
            return;

        let hasChanged: boolean;
        try {
            const outerKey = PermissionsKeyGenerator.createKey(row);
            const innerKey = PermissionsKeyGenerator.createAllowDenyKey(row);
            const deletedMapKey = `${outerKey}:${innerKey}`;

            const previouslyDeleted = this.deletedAllowDenyRows.get(deletedMapKey);
            if (previouslyDeleted) {

                this.deletedAllowDenyRows.delete(deletedMapKey);
                let innerMap = this.allowDenyRows.get(outerKey);
                if (!innerMap) {
                    innerMap = new Map();
                    this.allowDenyRows.set(outerKey, innerMap);
                }
                innerMap.set(innerKey, previouslyDeleted);
                return previouslyDeleted;
            }

            let innerMap = this.allowDenyRows.get(outerKey);
            if (innerMap != null) {
                const existing = innerMap.get(innerKey);
                if (existing != null) {
                    return existing;
                }
            } else {
                innerMap = new Map();
                this.allowDenyRows.set(outerKey, innerMap);
            }

            innerMap.set(innerKey, row);
            hasChanged = true;
            return row;

        } finally {
            this.fireChangedDataCallbacks(hasChanged);
        }
    }


    deleteAllowDenyRow(row: RowPermsAllowDeny) {
        if (row == null)
            return;

        try {
            const outerKey = PermissionsKeyGenerator.createKey(row);
            const innerKey = PermissionsKeyGenerator.createAllowDenyKey(row);
            const deletedMapKey = `${outerKey}:${innerKey}`;

            const innerMap = this.allowDenyRows.get(outerKey);

            if (innerMap && innerMap.has(innerKey)) {
                innerMap.delete(innerKey);

                if (innerMap.size === 0) {
                    this.allowDenyRows.delete(outerKey);
                }

                if (row._appending !== false)
                    return;

                this.deletedAllowDenyRows.set(deletedMapKey, row);
            }
        } finally {
            this.fireChangedDataCallbacks();
        }
    }

    async post() {
        const release = await this.postMutex.acquire();
        try {
            const postedData: ModelRow[] = [];
            const body: any = {};

            const addField = (key: string, array: ModelRow[]) => {
                if (!ArrayUtil.isEmptyArray(array)) {
                    body[key] = array;
                    postedData.push(...array);
                }
            };

            addField("perm_secure_item_rows", this.getAppendingSecureItemRows());
            addField("perm_allow_deny_rows", this.getAppendingAllowDenyRows());
            addField("deleted_perm_secure_item_rows", Array.from(this.deletedSecureItems.values()));
            addField("deleted_perm_allow_deny_rows", Array.from(this.deletedAllowDenyRows.values()));

            if (ObjectUtil.isEmptyObject(body)) {
                return;
            }

            const response = await Api.post("common/perms-manager", JSON.stringify(body));
            this.setPermissionsDataFromResponse(response);
            this.clearLayoutCache(postedData);

        } finally {
            release();
            this.fireChangedDataCallbacks();
        }
    }

    private clearLayoutCache(rows: ModelRow[]) {
        rows.forEach(row => {
            const path = row.get("screen_class");
            if (path != RowPermsSecureItem.ACTION)
                Layout.clearCachedLayout(path);
        });
    }
}
